import { useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ModalWrapper } from '@/core/modal';
import { CloseIcon } from '@/ui/icons';
import { useLocalization } from '@/services/localizationService';
import { closeModalSelector } from '@/stores/modalStore';
import { requestedAiToolState } from '@/stores/editorStore';
import { mediaShownInComparerState } from '@/stores/mediaStore';
import { useTracker } from '@/core/tracking';
import chevronHorizontalIcon from '@/public/images/icons/chevron--horizontal.svg';
import beforeImage from '@/public/images/auto-color/before.jpg';
import afterImage from '@/public/images/auto-color/after.jpg';

/*
 * Post-conversion discovery: spotlight the Auto Color tool on the photo the user
 * just enhanced. The CTA hands the request to the image editor toolbar, which
 * selects Auto Color; "Back to my photo" simply dismisses.
 */
export const AUTO_COLOR_TOOL = 'color_enhance';

interface Props {
  id: string;
  [x: string]: any;
}

export default function AutoColorDiscoveryModal({ id, ...rest }: Props) {
  const closeModal = useSetRecoilState(closeModalSelector);
  const setRequestedAiTool = useSetRecoilState(requestedAiToolState);
  const mediaInComparer = useRecoilValue(mediaShownInComparerState);
  const { t, data } = useLocalization();
  const { track } = useTracker();

  const heading = data.result.autoColorDiscoveryTitle;
  const body = data.result.autoColorDiscoveryBody;
  // Set when a CTA is chosen so the unmount cleanup does not also emit `dismissed`.
  const handledRef = useRef(false);

  useEffect(() => {
    track('auto_color_discovery', 'shown');
    return () => {
      if (!handledRef.current) {
        track('auto_color_discovery', 'dismissed');
      }
    };
  }, [track]);

  const onExplore = () => {
    handledRef.current = true;
    track('auto_color_discovery', 'explore_tapped');
    // Only hand the request to the editor when a photo is on screen; otherwise
    // there is nothing to apply Auto Color to.
    if (mediaInComparer.id) {
      setRequestedAiTool(AUTO_COLOR_TOOL);
    }
    closeModal(id);
  };

  const onBack = () => {
    handledRef.current = true;
    track('auto_color_discovery', 'back_tapped');
    closeModal(id);
  };

  return (
    <ModalWrapper
      label={heading}
      className="w-[500px] max-w-[calc(100vw-2rem)] p-5 pt-4 pb-2 text-center text-black"
      {...rest}
    >
      <div className="relative flex h-8 items-center justify-center">
        <button
          type="button"
          aria-label={data.common.dismiss}
          className="absolute left-0 grid h-8 w-8 place-items-center text-black"
          onClick={() => closeModal(id)}
        >
          <CloseIcon />
        </button>
      </div>

      {/* Auto Color proof: same subject and framing on both sides of the divider. */}
      <div className="relative mt-3 h-[264px] w-full overflow-hidden rounded-2xl bg-black">
        <img
          src={afterImage.src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: 'center 37%' }}
        />
        <img
          src={beforeImage.src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          style={{ clipPath: 'inset(0 50% 0 0)', objectPosition: 'center 37%' }}
        />
        <span className="absolute top-3.5 left-3.5 rounded-full bg-overlay-black-60 px-3 py-1.5 SB12 text-white">
          {t(data.common.before)}
        </span>
        <span className="absolute top-3.5 right-3.5 rounded-full bg-overlay-black-60 px-3 py-1.5 SB12 text-white">
          {t(data.common.after)}
        </span>
        <div className="absolute top-0 bottom-0 left-1/2 w-0.5 -ml-px bg-white" />
        <img
          {...chevronHorizontalIcon}
          alt=""
          className="absolute top-1/2 left-1/2 h-9 w-9 -translate-x-1/2 -translate-y-1/2"
        />
      </div>

      <h2 className="mt-6 SB25">{t(heading)}</h2>
      <p className="mt-3 M16 whitespace-pre-line text-black/60">{t(body)}</p>

      <button
        type="button"
        className="btn btn--large btn--black mt-6 w-full"
        onClick={onExplore}
      >
        {t(data.result.autoColorDiscoveryExplore)}
      </button>
      <button
        type="button"
        className="btn btn--large btn--black mt-1 w-full border-none bg-transparent text-black"
        onClick={onBack}
      >
        {t(data.result.autoColorDiscoveryBack)}
      </button>
    </ModalWrapper>
  );
}
